import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import moment, { Moment } from "moment";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { QESDateInput } from "@app/shared/qes-standards/forms/DateInput";
import { ColumnConfig, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { MultiselectItem, MultiselectOnChangeResponse, QESMultiSelect } from "@app/shared/qes-standards/forms/Multiselect";
import { CheckboxStateEnum, QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import GeographyService from "@app/geography/geographyService";
import { BasicState } from "@app/geography/state";
import { SoftwareLicenseDetail, SoftwareProduct } from "@app/admin/client-license/clientLicenseModels";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";

interface AddProductPanelProps {
  licenseId: number;
  licensedProducts: SoftwareLicenseDetail[];
  unlicensedProducts: SoftwareProduct[];
  onAddProducts: (productIds: string[], copies: string, isEvaluation: boolean, isNational: boolean, licensedStates: string[], evalExpDate?: Moment) => void;
}

interface UserTableData {
  [wafflesKeyColumnName]: string;
  name: string;
  productId: string;
}

const userColumnConfig: ColumnConfig = {
  name: {
    displayText: "Name",
    testId: "name",
  },
};

const AddProductPanelContent: FC<AddProductPanelProps> = (props: AddProductPanelProps) => {
  const flyoutContext = useContext(FlyoutContext);
  const [expirationDate, setExpirationDate] = useState<Moment>(null);
  const [isExpiring] = useState(true);
  const [isNational, setIsNational] = useState(true);
  const [licensedStates, setLicensedStates] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [numCopies, setNumCopies] = useState("1");
  const [numCopiesValid, setNumCopiesValid] = useState(true);
  const [productTableData, setProductTableData] = useState<UserTableData[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);
  const [stateList, setStateList] = useState<BasicState[]>([]);
  const [stateOptions, setStateOptions] = useState<Record<string, MultiselectItem>>({});

  const geographyService = useRef(new GeographyService()).current;
  const licensedProductIds = useMemo(() => props.licensedProducts.filter(x => x.productId > 0).map((x) => x.productId.toString()), [props.licensedProducts]);

  useEffect(() => {
    if (!props.licenseId)
      return;

    const allLicensedExpirationDate = props.licensedProducts.every((val, d, arr) => val.evalExpirationDate === arr[0].evalExpirationDate) ? moment.utc(props.licensedProducts[0].evalExpirationDate).startOf("day") : null;
    const allLicensedNumCopies = props.licensedProducts.every((val, c, arr) => val.numCopies === arr[0].numCopies) ? props.licensedProducts[0].numCopies.toString() : "0";
    const allLicensedNational = props.licensedProducts.every((val) => val.licensedStates === undefined);
    const allLicensedStatesMatch = props.licensedProducts.every((val, _, arr) => val.licensedStates !== undefined && val.licensedStates === arr[0].licensedStates);
    const allLicensedStatesList = !allLicensedNational && allLicensedStatesMatch ? props.licensedProducts[0].licensedStates.split(",").map(s => s.trim()) : [];
    geographyService.getStatesForDroplist().then((newStateList) => {
      setExpirationDate(allLicensedExpirationDate);
      setIsNational(allLicensedNational);
      setLicensedStates(allLicensedStatesList);
      setLoading(false);
      setNumCopies(allLicensedNumCopies);
      setProductTableData(createProductWafflesTable(props.unlicensedProducts));
      setSelectedProducts(licensedProductIds);
      setStateList(newStateList);
      setStateOptions(getMultiSelectOptions(stateList, allLicensedStatesList));
    });
  }, []);

  const getMultiSelectOptions = (list: BasicState[], selectedItems: string[]) => {
    const options: { [key: string]: MultiselectItem } = {};
    list.forEach((o) => Object.assign(options,
      {
        [o.abbreviation]: {
          currentState: selectedItems.find((ls) => ls === o.abbreviation) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
          displayValue: `${o.abbreviation} - ${o.name}`
        }
      }));
    return options;
  }

  const createProductWafflesTable = (data: SoftwareProduct[]) => {
    return data.map((rec) => {
      /* oxlint-disable sort-keys */
      const productName = licensedProductIds.includes(rec.productId.toString()) ? `${rec.productName} - **LICENSED**` : `${rec.productName}`;
      return {
        key: rec.productId.toString(),
        productId: rec.productId.toString(),
        name: productName
      };
      /* oxlint-enable sort-keys */
    });
  }

  const handleCheckboxChange = (selectedKeys: string[]) => {
    const selectedProducts = props.licensedProducts.filter(p => selectedKeys.includes(p.productId.toString()));
    const allSelectedExpirationDate = selectedProducts.length > 0 && selectedProducts.every((val, d, arr) => val.evalExpirationDate === arr[0].evalExpirationDate) ? moment.utc(selectedProducts[0].evalExpirationDate).startOf("day") : null;
    const allSelectedNumCopies = selectedProducts.length > 0 && selectedProducts.every((val, c, arr) => val.numCopies === arr[0].numCopies) ? selectedProducts[0].numCopies.toString() : "0";
    const allSelectedNational = selectedProducts.length > 0 && selectedProducts.every((val) => val.licensedStates === undefined);
    const allSelectedStatesMatch = selectedProducts.length > 0 && selectedProducts.every((val, _, arr) => val.licensedStates !== undefined && val.licensedStates === arr[0].licensedStates);
    const allSelectedStatesList = !allSelectedNational && allSelectedStatesMatch ? selectedProducts[0].licensedStates.split(",").map(s => s.trim()) : [];

    setExpirationDate(allSelectedExpirationDate);
    setIsNational(allSelectedNational);
    setLicensedStates(allSelectedStatesList);
    setNumCopies(allSelectedNumCopies);
    setSelectedProducts(selectedKeys);
    setStateOptions(getMultiSelectOptions(stateList, allSelectedStatesList));
  }

  const handleCopiesChange = (text: string) => {
    const numRegex = /(^\d+$|^$)/g;
    const newNumCopies = numRegex.test(text) ? text : numCopies;

    setNumCopies(newNumCopies);
    setNumCopiesValid(!!newNumCopies && parseInt(newNumCopies) > 0);
  }

  const handleEvalExpDateChange = (date: string) => {
    let newExpirationDate = moment.utc(date).startOf("day");

    if (newExpirationDate < moment().startOf("day"))
      newExpirationDate = moment().startOf("day");

    setExpirationDate(newExpirationDate);
  }

  const handleLicensedStateChanged = (response: MultiselectOnChangeResponse) => {
    const selectedStates = response.checkedKeys;
    const newStates = selectedStates.map((state) => {
      const matchedStateModel = stateList.find((x) => x.abbreviation === state);
      return matchedStateModel;
    });

    const abbrevs = newStates.map(x => x.abbreviation);

    setLicensedStates(abbrevs);
    setStateOptions(getMultiSelectOptions(stateList, abbrevs));
  }

  const handleSelectAll = () => {
    const abbrevs = stateList.map(x => x.abbreviation);

    setLicensedStates(abbrevs);
    setStateOptions(getMultiSelectOptions(stateList, abbrevs));
  }

  const handleAddClick = () => {
    const evalExpDate = isExpiring ? expirationDate : null;
    const newLicensedStates = isNational ? [] : licensedStates;

    props.onAddProducts(selectedProducts, numCopies, isExpiring, isNational, newLicensedStates, evalExpDate);
    flyoutContext.handleCloseFlyout();
  }

  return (
    <>
      {loading ? <LoadingSpinner /> :
        <>
          <Waffles<UserTableData>
            columns={userColumnConfig}
            data={productTableData}
            showCheckboxes={true}
            selectedKeys={selectedProducts}
            handleCheckboxChange={handleCheckboxChange}
            testId="add-product-panel-content__product-grid-waffles-table"
          />
          <QESTextInput
            containerClassName="mt-2 col-2 p-0"
            label="Copies"
            testId="add-product-panel-content__copies"
            required={true}
            isValid={numCopiesValid}
            value={numCopies}
            onChange={(e) => handleCopiesChange(e.target.value)} />
          <QESDateInput
            containerClassName="client-licenses__date-input col-2 p-0"
            min={moment().format("YYYY-MM-DD")}
            label="Expiration Date"
            testId="add-product-panel-content__eval-exp-date"
            onChange={(e) => handleEvalExpDateChange(e.target.value)}
            value={moment(expirationDate).format("YYYY-MM-DD")}
            required={isExpiring}
            isValid={isExpiring && !!expirationDate}
          />
          <QESCheckbox
            label="National"
            testId="add-product-panel-content__is-national"
            checked={isNational}
            onChange={(e) => setIsNational(e.target.checked)} />
          {!isNational ?
            <>
              <QESMultiSelect
                containerClassName="col-8 mb-3"
                errorText="At least one state must be selected."
                label="Licensed States"
                onChange={handleLicensedStateChanged}
                options={stateOptions}
                required={!isNational}
                isValid={!isNational && licensedStates.length > 0}
                testId="add-product-panel-content__licensed-states" />
              <QESButton
                qesTestId="add-product-panel-content__select-all"
                qesType="secondary"
                onClick={() => handleSelectAll()}
              >
                Select All
              </QESButton>
              <QESButton
                qesTestId="add-product-panel-content__clear-all"
                qesType="secondary"
                onClick={() => {
                  setLicensedStates([]);
                  setStateOptions(getMultiSelectOptions(stateList, []));
                }}
              >
                Clear All
              </QESButton>
            </> : null
          }
          <FlyoutFooter
            submitButton={<QESButton
              qesTestId="add-product-panel-content__confirm"
              className="me-2"
              qesType="primary"
              onClick={() => { handleAddClick(); }}
              disabled={selectedProducts.length === 0 || !numCopiesValid || (isExpiring && !expirationDate) || (!isNational && licensedStates.length === 0)}
            >
              Update
            </QESButton>
            }
          />
        </>
      }
    </>
  );
}

export default AddProductPanelContent;
